import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip } from '@heroui/react';
import { DataGrid } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { Icon } from '@iconify/react';
import type { ShiftRegisterItem } from '@/api/models';
import { formatGel, getNodeTestIdProp } from '@/utils';
import {
    discrepancyTone,
    formatShiftTime,
    SHIFT_STATUS_META,
} from '../../shiftsMeta';

type ShiftRegisterTableProps = {
    rows: ShiftRegisterItem[];
    onOpen: (shiftId: string) => void;
    /** Starts closing an open shift (SEL-31). */
    onCloseShift: (row: ShiftRegisterItem) => void;
};

const money = (value: number | null): string =>
    value === null ? '—' : formatGel(value);

const TONE_CLASS = {
    danger: 'text-danger',
    warning: 'text-warning',
    success: 'text-success',
} as const;

/**
 * The shift rows (render-only), in the order the hook gives them. Pressing a
 * row, or Enter on it, opens that shift's ledger summary; an open row also
 * offers "Close shift".
 */
export const ShiftRegisterTable: React.FC<ShiftRegisterTableProps> = ({
    rows,
    onOpen,
    onCloseShift,
}) => {
    const { t } = useTranslation('pages');

    const columns = React.useMemo<DataGridColumn<ShiftRegisterItem>[]>(
        () => [
            {
                id: 'seller',
                header: t('shifts.columns.seller'),
                isRowHeader: true,
                cell: (item) => (
                    <span
                        className="truncate text-sm font-medium text-foreground"
                        {...getNodeTestIdProp(`shift-register-row-${item.id}`)}
                    >
                        {item.openedByName ?? t('shifts.unknownSeller')}
                    </span>
                ),
            },
            {
                id: 'openedAt',
                header: t('shifts.columns.openedAt'),
                width: 150,
                cell: (item) => (
                    <span className="text-sm text-muted tabular-nums">
                        {formatShiftTime(item.openedAt)}
                    </span>
                ),
            },
            {
                id: 'closedAt',
                header: t('shifts.columns.closedAt'),
                width: 150,
                cell: (item) => (
                    <span className="text-sm text-muted tabular-nums">
                        {formatShiftTime(item.closedAt)}
                    </span>
                ),
            },
            {
                id: 'expected',
                header: t('shifts.columns.expected'),
                width: 130,
                align: 'end',
                cell: (item) => (
                    <span className="text-sm tabular-nums">
                        {money(item.expectedAtClose)}
                    </span>
                ),
            },
            {
                id: 'counted',
                header: t('shifts.columns.counted'),
                width: 130,
                align: 'end',
                cell: (item) => (
                    <span className="text-sm tabular-nums">
                        {money(item.closingCount)}
                    </span>
                ),
            },
            {
                id: 'difference',
                header: t('shifts.columns.difference'),
                width: 120,
                align: 'end',
                cell: (item) => {
                    const tone = discrepancyTone(item.closeDiscrepancy);
                    return (
                        <span
                            className={`text-sm font-semibold tabular-nums ${tone ? TONE_CLASS[tone] : 'text-muted'}`}
                            {...getNodeTestIdProp(
                                `shift-register-row-difference-${item.id}`,
                            )}
                        >
                            {money(item.closeDiscrepancy)}
                        </span>
                    );
                },
            },
            {
                id: 'status',
                header: t('shifts.columns.status'),
                width: 110,
                cell: (item) => {
                    const meta = SHIFT_STATUS_META[item.status];
                    return (
                        <Chip color={meta.color} size="sm" variant="soft">
                            {t(meta.labelKey)}
                        </Chip>
                    );
                },
            },
            {
                id: 'actions',
                header: t('shifts.columns.actions'),
                width: 140,
                align: 'end',
                cell: (item) =>
                    item.status === 'Open' ? (
                        <Button
                            {...getNodeTestIdProp(
                                `shift-register-row-close-${item.id}`,
                            )}
                            onPress={() => onCloseShift(item)}
                            size="sm"
                            variant="danger-soft"
                        >
                            {t('shifts.close.action')}
                        </Button>
                    ) : null,
            },
        ],
        [t, onCloseShift],
    );

    return (
        <div {...getNodeTestIdProp('shift-register-grid')}>
            <DataGrid<ShiftRegisterItem>
                aria-label={t('shifts.tableAria')}
                columns={columns}
                data={rows}
                getRowId={(item) => item.id}
                onRowAction={(key) => onOpen(String(key))}
                renderEmptyState={() => (
                    <div
                        className="flex flex-col items-center gap-3 py-12 text-muted"
                        {...getNodeTestIdProp('shift-register-empty')}
                    >
                        <Icon
                            className="size-8 text-muted"
                            icon="solar:clock-circle-bold-duotone"
                        />
                        <span className="text-sm">
                            {t('shifts.emptyTitle')}
                        </span>
                        <span className="text-xs">{t('shifts.emptyHint')}</span>
                    </div>
                )}
                scrollContainerClassName="max-h-[600px] overflow-y-auto"
                variant="primary"
            />
        </div>
    );
};
